import { useAppSelector } from '@/redux/hooks/useAppSelector';
import React from 'react';
import { userSelector } from '@/redux/user/user.selectors';
import { IconButton } from '@/shared/IconButton';
import { AuthToken } from '@/types/models';
import { showToast } from '@/utils/showToast';
import { disableAuthToken } from '@/redux/user/user.thunks';
import { useAppDispatch } from '@/redux/hooks/useAppDispatch';
import { Button } from '@/shared/Button';
import { openAddAuthTokenModal } from '@/redux/modal/modal.slice';
import { minervaRootSelector } from '@/redux/configuration/configuration.selectors';

export const ProfileAuthTokensModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const { authTokens } = useAppSelector(userSelector);

  function invalidateToken(authToken: AuthToken) {
    return async () => {
      await dispatch(disableAuthToken(authToken));
    };
  }

  function openAddTokenModal(): void {
    dispatch(openAddAuthTokenModal());
  }

  const minervaRootUrl = useAppSelector(minervaRootSelector);

  const baseUrl = minervaRootUrl ? `${minervaRootUrl}api/` : 'https://<YOUR_DOMAIN>/minerva/api/';
  const curlCode = `curl \\\n  '${baseUrl}projects/' \\\n  -X GET -H "Authorization: Bearer <API_TOKEN>"`;
  return (
    <div className="h-full w-[600px] border border-t-[#E1E0E6] p-[24px]">
      <h1 className="mb-1 font-bold">How to use: </h1>
      <p>
        Tokens are used in API calls via &apos;Authorization: Bearer&apos; header. <br />
        Example:
      </p>
      <pre className="rounded bg-gray-100 p-2 text-sm">{curlCode}</pre>
      <table className="mt-5 w-full text-left text-sm text-gray-500 dark:text-gray-400 rtl:text-right">
        <thead className="bg-gray-50 text-xs uppercase text-gray-700 dark:bg-gray-700 dark:text-gray-400">
          <tr>
            <th scope="col" className="px-6 py-2.5">
              <div className="flex items-center">Name</div>
            </th>
            <th scope="col" className="px-6 py-2.5">
              <div className="flex items-center">Token</div>
            </th>
            <th scope="col" className="px-6 py-2.5">
              <div className="flex items-center">Expiration date</div>
            </th>
          </tr>
        </thead>
        <tbody>
          {authTokens &&
            authTokens.map(authToken => (
              <tr
                key={authToken.id}
                className="border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
              >
                <th
                  scope="row"
                  className="whitespace-nowrap px-6 py-2 font-medium text-gray-900 dark:text-white"
                >
                  {authToken.name}{' '}
                </th>
                <td className="px-6 py-2">
                  <div className="flex max-w-[200px] items-center">
                    {authToken.token !== '***' && (
                      <IconButton
                        icon="copy"
                        className="h-auto w-auto bg-transparent p-0 "
                        title="Copy to clipboard"
                        onClick={async () => {
                          await navigator.clipboard.writeText(authToken.token);
                          showToast({ type: 'success', message: 'Token copied to clipboard' });
                        }}
                      />
                    )}
                    <span className="truncate"> {authToken.token} </span>
                  </div>
                </td>
                <td className={!authToken.active ? 'px-6 py-2 text-red-500' : 'px-6 py-2'}>
                  <div className="flex items-center">
                    {authToken.active && new Date(authToken.expirationDate) < new Date() && (
                      <span className="text-red-500">Expired</span>
                    )}

                    {(!authToken.active || new Date(authToken.expirationDate) > new Date()) && (
                      <span>{authToken.active ? authToken.expirationDate : 'Inactive'}</span>
                    )}
                    <IconButton
                      icon="trash"
                      className="h-auto w-auto bg-transparent p-0 "
                      classNameIcon="group-hover:fill-red-700 group-active:fill-red-700 fill-red-600"
                      title="Invalidate token"
                      onClick={invalidateToken(authToken)}
                    />
                  </div>
                </td>
              </tr>
            ))}
        </tbody>
      </table>
      <Button
        className="w-full justify-center text-base font-medium"
        onClick={() => openAddTokenModal()}
      >
        Create token
      </Button>
    </div>
  );
};
